import React from "react";

import { JollyRancherChip } from "veneer/Chip";
import { CheckFilledIcon, DownloadingIcon, UpdateIcon } from "veneer/SvgIcon";
import Tooltip from "veneer/Tooltip";

import { upgradablePlayerVersionsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/player-versions/selectors";
import { useAppSelector } from "cortex/ui/src/hooks";

import type { Device } from "../model";
import { getDeviceVersionStatusChipState } from "../utils";

interface DeviceVersionStatusChipProps {
  device: Device;
  onUpgradeClick?: () => void;
  pendingChipDataTestId?: string;
  upToDateChipDataTestId?: string;
  updateAvailableChipDataTestId?: string;
}

export default function DeviceVersionStatusChip({
  device,
  onUpgradeClick,
  pendingChipDataTestId,
  upToDateChipDataTestId,
  updateAvailableChipDataTestId,
}: DeviceVersionStatusChipProps) {
  const availableVersions = useAppSelector(upgradablePlayerVersionsSelector);
  const versionStatus = getDeviceVersionStatusChipState(
    device,
    availableVersions,
  );

  if (!versionStatus) {
    return null;
  }

  if (versionStatus.pending) {
    return (
      <Tooltip title={versionStatus.pendingTooltipTitle ?? ""}>
        <JollyRancherChip
          color="teal"
          dataTestId={pendingChipDataTestId}
          icon={<DownloadingIcon size="xsmall" />}
          iconPosition="start"
          label={device.current_version}
        />
      </Tooltip>
    );
  }

  if (versionStatus.updateAvailable) {
    return (
      <Tooltip title="Upgrade available">
        <JollyRancherChip
          clickable={Boolean(onUpgradeClick)}
          color="yellow"
          dataTestId={updateAvailableChipDataTestId}
          icon={<UpdateIcon size="xsmall" />}
          iconPosition="start"
          label={device.current_version}
          onClick={onUpgradeClick}
          stopPropagation={Boolean(onUpgradeClick)}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={versionStatus.upToDateTooltipTitle}>
      <JollyRancherChip
        color="green"
        dataTestId={upToDateChipDataTestId}
        icon={<CheckFilledIcon size="xsmall" />}
        iconPosition="start"
        label={device.current_version}
      />
    </Tooltip>
  );
}
